import { getAuthDiagnostic } from '../auth/authDiagnostics';
const WC_PROJECT_ID_FALLBACK = '1cd86059b40f95f9fba2099beabf3d7a';
const projectId = process.env.VITE_WALLETCONNECT_PROJECT_ID
    || process.env.REACT_APP_WALLETCONNECT_PROJECT_ID
    || process.env.WALLETCONNECT_PROJECT_ID
    || WC_PROJECT_ID_FALLBACK;
console.log('[WalletConfig] Project ID available:', !!projectId);
export const metadata = {
    name: 'Dwinity Dashboard',
    description: 'Own, manage and monetize your personal data',
    url: typeof window !== 'undefined' ? window.location.origin : 'https://dwdashboard.dev.dwinity.com',
    icons: ['https://dwdashboard.dev.dwinity.com/dwinity-logo.png']
};
export const avalancheNetwork = {
    id: 43114,
    name: 'Avalanche',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    rpcUrls: {
        default: { http: ['https://api.avax.network/ext/bc/C/rpc'] }
    },
    blockExplorers: {
        default: { name: 'SnowTrace', url: 'https://snowtrace.io' }
    }
};
export const avalancheFujiNetwork = {
    id: 43113,
    name: 'Avalanche Fuji',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    rpcUrls: {
        default: { http: ['https://api.avax-test.network/ext/bc/C/rpc'] }
    },
    blockExplorers: {
        default: { name: 'SnowTrace Testnet', url: 'https://testnet.snowtrace.io' }
    },
    testnet: true
};
export const networks = [avalancheNetwork, avalancheFujiNetwork];
let appKitInstance = null;
let injectedWalletProvider = null;
// Once a live EIP-1193 provider has been selected, keep all account reads and
// signing requests on that exact provider session.  AppKit may replace the
// object returned by getWalletProvider() while restoring a cached pairing;
// silently switching providers would let the account displayed by one bridge
// authorize/sign through another.
let boundWalletProvider = null;
let appKitLoading = false;
let appKitLoadingStartedAt = 0;
let appKitResolvers = [];
const APPKIT_LOADING_TIMEOUT_MS = 15000;
export async function initAppKit() {
    if (appKitInstance)
        return appKitInstance;
    if (appKitLoading) {
        if (Date.now() - appKitLoadingStartedAt > APPKIT_LOADING_TIMEOUT_MS) {
            console.warn('[WalletConfig] AppKit loading stuck, resetting lock');
            appKitLoading = false;
            appKitResolvers.forEach(r => r(null));
            appKitResolvers = [];
        }
        else {
            return new Promise((resolve) => { appKitResolvers.push(resolve); });
        }
    }
    if (!projectId) {
        console.error('[WalletConfig] No projectId, cannot initialize AppKit');
        return null;
    }
    appKitLoading = true;
    appKitLoadingStartedAt = Date.now();
    try {
        console.log('[WalletConfig] Initializing AppKit...');
        const [{ createAppKit }, { EthersAdapter }] = await Promise.all([
            import('@reown/appkit'),
            import('@reown/appkit-adapter-ethers'),
        ]);
        appKitInstance = createAppKit({
            adapters: [new EthersAdapter()],
            networks: [avalancheFujiNetwork],
            defaultNetwork: avalancheFujiNetwork,
            projectId,
            metadata,
            themeMode: 'dark',
            themeVariables: {
                '--w3m-accent': '#e91e8c',
                '--w3m-color-mix': '#1a1a2e',
                '--w3m-color-mix-strength': 40
            },
            features: {
                analytics: false,
                email: false,
                socials: false
            },
            allowUnsupportedChain: true,
        });
        console.log('[WalletConfig] AppKit initialized successfully');
        appKitResolvers.forEach(r => r(appKitInstance));
        appKitResolvers = [];
        return appKitInstance;
    }
    catch (err) {
        console.error('[WalletConfig] AppKit init failed:', getAuthDiagnostic(err));
        appKitLoading = false;
        appKitResolvers.forEach(r => r(null));
        appKitResolvers = [];
        return null;
    }
}
export function getAppKitSync() {
    return appKitInstance;
}
export function getAppKitModal() {
    return appKitInstance;
}
function toHex(message) {
    return '0x' + Array.from(new TextEncoder().encode(message))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}
export function getWalletProvider() {
    if (injectedWalletProvider)
        return injectedWalletProvider;
    if (boundWalletProvider)
        return boundWalletProvider;
    if (!appKitInstance)
        return null;
    return appKitInstance.getWalletProvider?.() || null;
}
export function setInjectedWalletProvider(provider) {
    injectedWalletProvider = provider || null;
    if (provider) {
        boundWalletProvider = provider;
    }
    else if (boundWalletProvider === provider) {
        boundWalletProvider = null;
    }
}
export function clearBoundWalletProvider(provider) {
    if (!provider || boundWalletProvider === provider) {
        boundWalletProvider = null;
    }
}
function normalizeAccounts(accounts) {
    if (!Array.isArray(accounts))
        return [];
    return accounts.filter((account) => typeof account === 'string' && account.length > 0);
}
/**
 * Read the account from the selected EIP-1193 session without prompting.
 *
 * Automatic restore must only use eth_accounts.  Callers handling an
 * explicit connect gesture may opt into eth_requestAccounts when no account
 * is already authorized by this site.
 */
export async function ensureWalletConnection(options = {}) {
    const provider = options.provider || getWalletProvider();
    if (!provider || typeof provider.request !== 'function') {
        return { provider: null, address: null };
    }
    let accounts = normalizeAccounts(await provider.request({ method: 'eth_accounts' }));
    if (accounts.length === 0 && options.requestAccounts) {
        accounts = normalizeAccounts(await provider.request({ method: 'eth_requestAccounts' }));
    }
    if (accounts.length > 0) {
        boundWalletProvider = provider;
        return { provider, address: accounts[0] };
    }
    return { provider, address: null };
}
/**
 * Subscribe to the account/disconnect events emitted by the exact provider
 * used for this session.  AppKit's account store is useful UI state, but the
 * EIP-1193 provider is authoritative for account changes.
 */
export function subscribeWalletProvider(callback, provider = getWalletProvider()) {
    if (!provider || typeof provider.on !== 'function')
        return () => { };
    const handleAccountsChanged = (accounts) => {
        const normalizedAccounts = normalizeAccounts(accounts);
        callback({
            isConnected: normalizedAccounts.length > 0,
            address: normalizedAccounts[0],
            confirmed: true,
        });
    };
    const handleDisconnect = () => {
        callback({ isConnected: false, confirmed: true });
    };
    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('disconnect', handleDisconnect);
    return () => {
        const remove = typeof provider.removeListener === 'function'
            ? provider.removeListener.bind(provider)
            : typeof provider.off === 'function'
                ? provider.off.bind(provider)
                : null;
        remove?.('accountsChanged', handleAccountsChanged);
        remove?.('disconnect', handleDisconnect);
    };
}
export async function testProviderHealth() {
    try {
        const provider = getWalletProvider();
        if (!provider) {
            console.log('[WalletConfig] testProviderHealth: no provider available');
            return { healthy: false, provider: null };
        }
        await Promise.race([
            provider.request({ method: 'eth_chainId' }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Provider health check timed out')), 10000))
        ]);
        console.log('[WalletConfig] testProviderHealth: healthy');
        return { healthy: true, provider };
    }
    catch (e) {
        console.warn('[WalletConfig] testProviderHealth: unhealthy -', getAuthDiagnostic(e));
        return { healthy: false, provider: null };
    }
}
function isIOS() {
    if (typeof navigator === 'undefined')
        return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
function waitForVisibility(timeoutMs = 30000) {
    if (!document.hidden)
        return Promise.resolve();
    return new Promise(resolve => {
        const handler = () => {
            if (!document.hidden) {
                document.removeEventListener('visibilitychange', handler);
                setTimeout(resolve, 800);
            }
        };
        document.addEventListener('visibilitychange', handler);
        setTimeout(() => {
            document.removeEventListener('visibilitychange', handler);
            resolve();
        }, timeoutMs);
    });
}
export async function signMessage(message, account) {
    const ios = isIOS();
    const MAX_RETRIES = ios ? 5 : 4;
    const RETRY_DELAYS = ios ? [0, 4000, 6000, 8000, 10000] : [0, 3000, 5000, 8000];
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            if (attempt > 1) {
                const delayMs = RETRY_DELAYS[attempt - 1] || 5000;
                console.log(`[WalletConfig] signMessage: retry attempt ${attempt}/${MAX_RETRIES}, waiting ${delayMs}ms...`);
                await new Promise(r => setTimeout(r, delayMs));
            }
            await waitForVisibility();
            if (ios) {
                await new Promise(r => setTimeout(r, 300));
            }
            let provider = getWalletProvider();
            console.log(`[WalletConfig] signMessage attempt ${attempt}/${MAX_RETRIES}: provider available:`, !!provider, ios ? '(iOS)' : '');
            if (!provider) {
                if (attempt < MAX_RETRIES) {
                    console.log('[WalletConfig] No provider, attempting AppKit restore...');
                    const modal = await initAppKit();
                    if (modal) {
                        await new Promise(r => setTimeout(r, 1000));
                        provider = getWalletProvider();
                    }
                }
                if (!provider) {
                    throw new Error('No wallet provider available — wallet may have disconnected');
                }
            }
            const hexMessage = toHex(message);
            const signature = await Promise.race([
                provider.request({
                    method: 'personal_sign',
                    params: [hexMessage, account]
                }),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Signature request timed out')), ios ? 120000 : 60000))
            ]);
            if (!signature) {
                throw new Error('No signature returned');
            }
            console.log(`[WalletConfig] signMessage attempt ${attempt} succeeded`);
            return signature;
        }
        catch (err) {
            const errMsg = err instanceof Error ? err.message : String(err);
            console.error(`[WalletConfig] signMessage attempt ${attempt}/${MAX_RETRIES} failed:`, getAuthDiagnostic(err));
            if (err?.code === 4001 || errMsg.includes('rejected') || errMsg.includes('denied')) {
                throw new Error('Signature rejected by user');
            }
            if (errMsg.includes('operation') && errMsg.includes('reason') && attempt < MAX_RETRIES) {
                console.log(`[WalletConfig] iOS operation-specific error, waiting extra before retry...`);
                await new Promise(r => setTimeout(r, ios ? 5000 : 3000));
                await waitForVisibility();
                continue;
            }
            if (attempt >= MAX_RETRIES) {
                const wrappedError = new Error(`[WalletSign] ${errMsg}`);
                wrappedError.isWalletSignError = true;
                throw wrappedError;
            }
        }
    }
    const finalError = new Error('[WalletSign] signMessage failed after all retries');
    finalError.isWalletSignError = true;
    throw finalError;
}
export async function batchSignMessages(messages, account) {
    if (messages.length === 0)
        return [];
    if (messages.length === 1) {
        const sig = await signMessage(messages[0], account);
        return [sig];
    }
    console.log(`[WalletConfig] batchSignMessages: sending ${messages.length} signature requests near-simultaneously...`);
    await waitForVisibility();
    const ios = isIOS();
    const android = /android/i.test(navigator.userAgent);
    if (ios) {
        await new Promise(r => setTimeout(r, 300));
    }
    const provider = getWalletProvider();
    if (!provider) {
        throw new Error('No wallet provider available for batch signing');
    }
    const timeoutMs = ios ? 180000 : 120000;
    const STAGGER_MS = android ? 50 : (ios ? 800 : 200);
    const promises = [];
    for (let index = 0; index < messages.length; index++) {
        if (index > 0) {
            await new Promise(r => setTimeout(r, STAGGER_MS));
        }
        const hexMessage = toHex(messages[index]);
        const signPromise = provider.request({
            method: 'personal_sign',
            params: [hexMessage, account]
        });
        console.log(`[WalletConfig] batchSignMessages: request ${index + 1}/${messages.length} sent (stagger: ${STAGGER_MS}ms)`);
        promises.push(Promise.race([
            signPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error(`Batch signature ${index + 1} timed out`)), timeoutMs))
        ]));
    }
    try {
        const results = await Promise.all(promises);
        console.log(`[WalletConfig] batchSignMessages: all ${messages.length} signatures received`);
        return results;
    }
    catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        console.error('[WalletConfig] batchSignMessages failed:', getAuthDiagnostic(err));
        if (err?.code === 4001 || errMsg.includes('rejected') || errMsg.includes('denied')) {
            throw new Error('Signature rejected by user');
        }
        console.log('[WalletConfig] batchSignMessages: falling back to sequential signing...');
        const results = [];
        for (let i = 0; i < messages.length; i++) {
            console.log(`[WalletConfig] Sequential fallback: signing message ${i + 1}/${messages.length}`);
            const sig = await signMessage(messages[i], account);
            results.push(sig);
        }
        return results;
    }
}
export async function getConnectedAddress() {
    const provider = getWalletProvider();
    if (!provider)
        return null;
    try {
        const accounts = await provider.request({ method: 'eth_accounts' });
        return accounts?.[0] || null;
    }
    catch {
        return null;
    }
}
export async function forceReconnect() {
    const android = /android/i.test(navigator.userAgent);
    console.log('[WalletConfig] Attempting AppKit session restore...', android ? '(Android)' : '');
    try {
        const modal = await initAppKit();
        if (!modal)
            return false;
        const settleMs = android ? 2000 : 1000;
        await new Promise(r => setTimeout(r, settleMs));
        let provider = getWalletProvider();
        if (!provider && android) {
            console.log('[WalletConfig] No provider on first try (Android), waiting extra...');
            await new Promise(r => setTimeout(r, 2000));
            provider = getWalletProvider();
        }
        if (!provider)
            return false;
        const accounts = await Promise.race([
            provider.request({ method: 'eth_accounts' }),
            new Promise((resolve) => setTimeout(() => resolve(null), 5000))
        ]);
        if (accounts && accounts.length > 0) {
            console.log('[WalletConfig] Session restored!');
            return true;
        }
        return false;
    }
    catch (e) {
        console.warn('[WalletConfig] Session restore failed:', getAuthDiagnostic(e));
        return false;
    }
}
export function hasExistingWcPairing() {
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key)
                continue;
            if (key.startsWith('wc@2:core:')) {
                const val = localStorage.getItem(key);
                if (val && val.includes('"active"')) {
                    console.log('[WalletConfig] Found active WC pairing in localStorage');
                    return true;
                }
            }
            if (key === '@appkit/connected_connector' || key === '@appkit/wallet_id') {
                const val = localStorage.getItem(key);
                if (val) {
                    console.log('[WalletConfig] Found AppKit connector state');
                    return true;
                }
            }
            if (key.startsWith('wc@2:client:') && key.includes('session')) {
                const val = localStorage.getItem(key);
                if (val && val.length > 10) {
                    console.log('[WalletConfig] Found WC session data in localStorage');
                    return true;
                }
            }
        }
        return false;
    }
    catch {
        return false;
    }
}
export { projectId };
export default { projectId, metadata, signMessage, testProviderHealth, forceReconnect, initAppKit, getAppKitSync, getAppKitModal, getConnectedAddress, hasExistingWcPairing };
